import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import StatusBadge from '../components/StatusBadge';
import { STATUTS_LIST } from '../utils/constants';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw, ChevronDown, Save, Edit2, X, Truck, Calendar, Clock, MapPin, Phone, User, Package, Euro, Camera, Upload, Trash2, ZoomIn, Download } from 'lucide-react';

const JOURS = [
  { key: 'lundi', label: 'Lundi' },
  { key: 'mardi', label: 'Mardi' },
  { key: 'mercredi', label: 'Mercredi' },
  { key: 'jeudi', label: 'Jeudi' },
  { key: 'vendredi', label: 'Vendredi' },
  { key: 'samedi', label: 'Samedi' },
  { key: 'dimanche', label: 'Dimanche' }
];

const STATUT_LIVRAISON_COLORS = {
  a_planifier: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'À planifier' },
  assignee: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Assignée' },
  en_cours: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'En cours' },
  livree: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Livrée' },
  planifiee: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Planifiée' },
};

export default function CommandeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [commande, setCommande] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStatutMenu, setShowStatutMenu] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [editLivraison, setEditLivraison] = useState(false);
  const [disponibilites, setDisponibilites] = useState(
    JOURS.map(j => ({ jour: j.key, actif: false, heure_debut: '09:00', heure_fin: '18:00' }))
  );
  const [dateLivraisonPrevue, setDateLivraisonPrevue] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [livreurs, setLivreurs] = useState([]);
  const [selectedLivreur, setSelectedLivreur] = useState('');
  const [assigningLivreur, setAssigningLivreur] = useState(false);

  const fetchCommande = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/commandes/${id}`);
      setCommande(res.data);
      setEditData({ 
        commentaires: res.data.commentaires || '', 
        relance: res.data.relance?.split('T')[0] || '', 
        livreur_id: res.data.livreur_id || '' 
      });
      if (res.data.disponibilites_livraison?.length > 0) {
        setDisponibilites(res.data.disponibilites_livraison);
      }
      setDateLivraisonPrevue(res.data.date_livraison_prevue || '');
    } catch { navigate(-1); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCommande(); }, [id]);

  // Fetch livreurs list
  useEffect(() => {
    api.get('/users').then(res => {
      setLivreurs((res.data || []).filter(u => u.role === 'livreur'));
    }).catch(() => {});
  }, []);

  // Set selected livreur from commande
  useEffect(() => {
    if (commande?.livreur_id) setSelectedLivreur(commande.livreur_id);
  }, [commande?.livreur_id]);

  const assignerLivreur = async () => {
    if (!selectedLivreur) { toast.error('Sélectionnez un livreur'); return; }
    setAssigningLivreur(true);
    try {
      await api.put(`/commandes/${id}/assigner-livreur`, { livreur_id: selectedLivreur });
      toast.success('Livreur assigné');
      fetchCommande();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setAssigningLivreur(false); }
  };

  // Fetch photos d'installation
  const fetchPhotos = async (clientId) => {
    try {
      const res = await api.get(`/documents/client/${clientId}`);
      // Filtrer uniquement les photos de cette commande
      const commandePhotos = res.data.filter(
        d => d.commande_id === id && d.type_doc === 'photo_installation'
      );
      setPhotos(commandePhotos);
    } catch (err) {
      console.error('Erreur chargement photos:', err);
    }
  };

  useEffect(() => {
    if (commande?.client_id) {
      fetchPhotos(commande.client_id);
    }
  }, [commande?.client_id, id]);

  // Upload photo(s) - supporte l'upload multiple et drag & drop
  const uploadFiles = async (files) => {
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (imageFiles.length !== files.length) {
      toast.error('Seules les images sont acceptées');
      if (imageFiles.length === 0) return;
    }

    setUploadingPhoto(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of imageFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('client_id', commande.client_id);
        formData.append('commande_id', id);
        formData.append('type_doc', 'photo_installation');

        await api.post('/documents/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        successCount++;
      } catch (err) {
        console.error(`Erreur upload ${file.name}:`, err);
        errorCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} photo${successCount > 1 ? 's' : ''} ajoutée${successCount > 1 ? 's' : ''}`);
      fetchPhotos(commande.client_id);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} photo${errorCount > 1 ? 's' : ''} non uploadée${errorCount > 1 ? 's' : ''}`);
    }
    setUploadingPhoto(false);
  };

  const handleUploadPhoto = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    await uploadFiles(files);
    e.target.value = '';
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (!files.length) return;
    await uploadFiles(files);
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };

  // Supprimer photo
  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    
    try {
      await api.delete(`/documents/${photoId}`);
      toast.success('Photo supprimée');
      setPhotos(photos.filter(p => p.id !== photoId));
      if (selectedPhoto?.id === photoId) setSelectedPhoto(null);
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Format date pour affichage photo
  const formatPhotoDate = (dateStr) => {
    if (!dateStr) return '';
    const [datePart] = dateStr.split('T');
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  };

  const changeStatut = async (newStatut) => {
    try {
      await api.put(`/commandes/${id}`, { statut: newStatut });
      toast.success('Statut mis à jour');
      setShowStatutMenu(false);
      fetchCommande();
    } catch { toast.error('Erreur'); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/commandes/${id}`, editData);
      toast.success('Commande mise à jour');
      setEditMode(false);
      fetchCommande();
    } catch { toast.error('Erreur'); }
  };

  const saveLivraison = async () => {
    try {
      await api.put(`/commandes/${id}`, {
        disponibilites_livraison: disponibilites,
        date_livraison_prevue: dateLivraisonPrevue || null,
        statut_livraison: dateLivraisonPrevue ? 'planifiee' : 'a_planifier'
      });
      toast.success('Livraison mise à jour');
      setEditLivraison(false);
      fetchCommande();
    } catch { toast.error('Erreur'); }
  };

  const updateDispo = (jour, field, value) => {
    setDisponibilites(prev => prev.map(d => 
      d.jour === jour ? { ...d, [field]: value } : d
    ));
  };

  const markAsLivree = async () => {
    try {
      await api.put(`/commandes/${id}`, { statut_livraison: 'livree' });
      toast.success('Marqué comme livré');
      fetchCommande();
    } catch { toast.error('Erreur'); }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price || 0);
  };

  if (loading) return <div className="p-8 flex items-center justify-center"><RefreshCw size={20} className="animate-spin text-gray-400" /></div>;
  if (!commande) return null;

  const inputCls = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)] bg-white";
  
  // Calculs des totaux
  const hasLignes = commande.lignes && commande.lignes.length > 0;
  const sousTotal = hasLignes 
    ? commande.lignes.reduce((sum, l) => sum + (l.prix_unitaire_ht * l.quantite), 0)
    : (commande.prix_unitaire_ht || 0) * (commande.quantite || 1);
  const fraisLivraison = commande.frais_livraison || 780;
  const totalHT = commande.total_ht || (sousTotal + fraisLivraison);
  const tva = commande.tva || (totalHT * 0.2);
  const totalTTC = commande.total_ttc || (totalHT + tva);

  // Client info
  const client = commande.client || {};

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="back-btn">
          <ArrowLeft size={18} className="text-gray-600" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-[hsl(222,47%,11%)]">{commande.numero}</h1>
          <p className="text-sm text-gray-500">{client.societe}</p>
        </div>
      </div>

      {/* Statut & Actions */}
      <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                onClick={() => setShowStatutMenu(!showStatutMenu)}
                className="flex items-center gap-2 px-3 py-2 border-2 border-gray-200 rounded-xl hover:border-gray-400 transition-colors"
                data-testid="statut-menu-btn"
              >
                <StatusBadge statut={commande.statut} />
                <ChevronDown size={14} className="text-gray-400" />
              </button>
              {showStatutMenu && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-xl shadow-lg z-10 overflow-hidden max-h-80 overflow-y-auto">
                  {STATUTS_LIST.map(s => (
                    <button
                      key={s.value}
                      onClick={() => changeStatut(s.value)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 ${commande.statut === s.value ? 'bg-gray-50 font-semibold' : ''}`}
                      data-testid={`statut-option-${s.value}`}
                    >
                      <span className={`w-2 h-2 rounded-full ${s.dot}`}></span>
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="text-sm text-gray-500">
              Commande du {new Date(commande.date_commande).toLocaleDateString('fr-FR')}
            </div>
          </div>
          <div className="flex gap-2">
            {editMode ? (
              <>
                <button onClick={saveEdit} className="flex items-center gap-1.5 bg-emerald-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-emerald-700" data-testid="save-commande-btn"><Save size={14} />Enregistrer</button>
                <button onClick={() => setEditMode(false)} className="flex items-center gap-1.5 border border-gray-200 px-3 py-2 rounded-lg text-sm hover:bg-gray-50"><X size={14} />Annuler</button>
              </>
            ) : (
              <button onClick={() => setEditMode(true)} className="flex items-center gap-1.5 border border-gray-200 px-3 py-2 rounded-lg text-sm hover:bg-gray-50 text-gray-700" data-testid="edit-commande-btn"><Edit2 size={14} />Modifier</button>
            )}
          </div>
        </div>

        {editMode && (
          <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Commentaires</label>
              <textarea className={inputCls + " resize-none h-20"} value={editData.commentaires} onChange={e => setEditData(p=>({...p,commentaires:e.target.value}))} data-testid="commentaires-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Date de relance</label>
              <input type="date" className={inputCls} value={editData.relance} onChange={e => setEditData(p=>({...p,relance:e.target.value}))} />
            </div>
          </div>
        )}

        {commande.commentaires && !editMode && (
          <div className="mt-4 p-3 bg-amber-50 rounded-xl border border-amber-200">
            <p className="text-xs font-medium text-amber-700 mb-1">Commentaire</p>
            <p className="text-sm text-amber-900">{commande.commentaires}</p>
          </div>
        )}
      </div>

      {/* Produits */}
      <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <Package size={18} className="text-gray-600" />
          Produits commandés
        </h3>
        
        {hasLignes ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-semibold text-gray-500 py-2 px-2">Référence</th>
                  <th className="text-left text-xs font-semibold text-gray-500 py-2 px-2">Désignation</th>
                  <th className="text-center text-xs font-semibold text-gray-500 py-2 px-2">Qté</th>
                  <th className="text-right text-xs font-semibold text-gray-500 py-2 px-2">Prix unit. HT</th>
                  <th className="text-right text-xs font-semibold text-gray-500 py-2 px-2">Total HT</th>
                </tr>
              </thead>
              <tbody>
                {commande.lignes.map((ligne, idx) => (
                  <tr key={ligne.reference || `ligne-${idx}`} className="border-b border-gray-50">
                    <td className="py-3 px-2">
                      <span className="font-mono text-sm font-semibold text-[hsl(222,47%,11%)]">{ligne.reference}</span>
                    </td>
                    <td className="py-3 px-2 text-sm text-gray-700">{ligne.designation}</td>
                    <td className="py-3 px-2 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 rounded-lg text-sm font-bold">{ligne.quantite}</span>
                    </td>
                    <td className="py-3 px-2 text-right text-sm text-gray-700">{formatPrice(ligne.prix_unitaire_ht)}</td>
                    <td className="py-3 px-2 text-right text-sm font-semibold text-gray-900">{formatPrice(ligne.prix_unitaire_ht * ligne.quantite)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
            <div className="w-12 h-12 bg-[hsl(222,47%,11%)] rounded-xl flex items-center justify-center">
              <Package size={20} className="text-white" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">{commande.modele || 'Produit'}</p>
              <p className="text-sm text-gray-500">Quantité : {commande.quantite || 1}</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-sm text-gray-500">Prix unitaire HT</p>
              <p className="font-bold text-gray-900">{formatPrice(commande.prix_unitaire_ht)}</p>
            </div>
          </div>
        )}
      </div>

      {/* Totaux */}
      <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <Euro size={18} className="text-gray-600" />
          Récapitulatif financier
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <p className="text-xs text-gray-500 mb-1">Sous-total HT</p>
            <p className="text-lg font-bold text-gray-900">{formatPrice(sousTotal)}</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <p className="text-xs text-gray-500 mb-1">Livraison HT</p>
            <p className="text-lg font-bold text-gray-900">{formatPrice(fraisLivraison)}</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <p className="text-xs text-gray-500 mb-1">TVA 20%</p>
            <p className="text-lg font-bold text-gray-900">{formatPrice(tva)}</p>
          </div>
          <div className="bg-[hsl(222,47%,11%)] rounded-xl p-4 text-center">
            <p className="text-xs text-white/70 mb-1">Total TTC</p>
            <p className="text-xl font-bold text-white">{formatPrice(totalTTC)}</p>
          </div>
        </div>
      </div>

      {/* Livraison */}
      <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Truck size={18} className="text-gray-600" />
            Livraison
          </h3>
          <div className="flex items-center gap-2">
            {commande.statut_livraison && (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUT_LIVRAISON_COLORS[commande.statut_livraison]?.bg} ${STATUT_LIVRAISON_COLORS[commande.statut_livraison]?.text}`}>
                {STATUT_LIVRAISON_COLORS[commande.statut_livraison]?.label}
              </span>
            )}
            {editLivraison ? (
              <>
                <button onClick={saveLivraison} className="flex items-center gap-1 bg-[hsl(222,47%,11%)] text-white px-3 py-1.5 rounded-lg text-sm"><Save size={14} />Enregistrer</button>
                <button onClick={() => setEditLivraison(false)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={16} /></button>
              </>
            ) : (
              <>
                {commande.statut_livraison !== 'livree' && (
                  <button onClick={markAsLivree} className="flex items-center gap-1 bg-emerald-500 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-emerald-600">
                    <Truck size={14} />Marquer livré
                  </button>
                )}
                <button onClick={() => setEditLivraison(true)} className="flex items-center gap-1 border border-gray-200 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-50">
                  <Edit2 size={14} />Modifier
                </button>
              </>
            )}
          </div>
        </div>

        {/* Infos client livraison */}
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin size={18} className="text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Adresse de livraison</p>
                <p className="font-semibold text-gray-900">{client.societe || '—'}</p>
                <p className="text-sm text-gray-600">{client.gerant || client.gerant_nom || ''}</p>
                {/* Check if using new 3-field format (code_postal_livraison/ville_livraison set) or old single-field format */}
                {(client.code_postal_livraison || client.ville_livraison) ? (
                  <>
                    {/* New format: separate fields */}
                    <p className="text-sm text-gray-700 mt-1">
                      {client.adresse_livraison || client.adresse || '—'}
                    </p>
                    <p className="text-sm text-gray-700">
                      {client.code_postal_livraison || client.code_postal} {client.ville_livraison || client.ville}
                    </p>
                  </>
                ) : (
                  <>
                    {/* Old format: adresse_livraison may contain full address - don't add CP/Ville again */}
                    <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">
                      {client.adresse_livraison || client.adresse || '—'}
                    </p>
                    {/* Only show CP/Ville if adresse_livraison is not set (fallback to adresse) */}
                    {!client.adresse_livraison && client.code_postal && client.ville && (
                      <p className="text-sm text-gray-700">
                        {client.code_postal} {client.ville}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Phone size={18} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Téléphones</p>
                {(client.tel_salon || client.telephone) && (
                  <p className="font-semibold text-gray-900">
                    📞 Salon : {client.tel_salon || client.telephone}
                  </p>
                )}
                {(client.tel_portable || client.mobile) && (
                  <p className="font-semibold text-gray-900">
                    📱 Portable : {client.tel_portable || client.mobile}
                  </p>
                )}
                {!client.tel_salon && !client.telephone && !client.tel_portable && !client.mobile && (
                  <p className="text-gray-400">—</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Date livraison prévue */}
        <div className="p-4 bg-blue-50 rounded-xl mb-4 border border-blue-100">
          <div className="flex items-center gap-2 mb-2">
            <Calendar size={16} className="text-blue-600" />
            <span className="text-sm font-medium text-blue-700">Date de livraison prévue</span>
          </div>
          {editLivraison ? (
            <input
              type="datetime-local"
              value={dateLivraisonPrevue}
              onChange={e => setDateLivraisonPrevue(e.target.value)}
              className={inputCls}
              data-testid="date-livraison-input"
            />
          ) : (
            <p className="text-lg font-semibold text-gray-900">
              {commande.date_livraison_prevue 
                ? (() => {
                    // Parse date without timezone conversion
                    const [datePart, timePart] = commande.date_livraison_prevue.split('T');
                    const [year, month, day] = datePart.split('-').map(Number);
                    const jours = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
                    const mois = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
                    // Calculate day of week using Zeller-like formula
                    const d = new Date(year, month - 1, day);
                    const jourSemaine = jours[d.getDay()];
                    const heure = timePart ? timePart.substring(0, 5) : '';
                    return `${jourSemaine} ${day} ${mois[month - 1]} ${year}${heure ? ` à ${heure}` : ''}`;
                  })()
                : '— Non planifiée —'}
            </p>
          )}
        </div>

        {/* Assignation Livreur */}
        <div className="p-4 bg-amber-50 rounded-xl mb-4 border border-amber-100" data-testid="livreur-assignment-section">
          <div className="flex items-center gap-2 mb-3">
            <Truck size={16} className="text-amber-600" />
            <span className="text-sm font-medium text-amber-700">Livreur assigné</span>
            {commande.statut_livraison && (
              <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full ${(STATUT_LIVRAISON_COLORS[commande.statut_livraison] || STATUT_LIVRAISON_COLORS.a_planifier).bg} ${(STATUT_LIVRAISON_COLORS[commande.statut_livraison] || STATUT_LIVRAISON_COLORS.a_planifier).text}`} data-testid="livraison-status-badge">
                {(STATUT_LIVRAISON_COLORS[commande.statut_livraison] || STATUT_LIVRAISON_COLORS.a_planifier).label}
              </span>
            )}
          </div>

          {commande.livreur_id && commande.livreur_nom ? (
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center">
                <User size={14} className="text-amber-700" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm" data-testid="assigned-livreur-name">{commande.livreur_nom}</p>
                <p className="text-xs text-gray-500">
                  {commande.statut_livraison === 'assignee' && 'En attente de confirmation'}
                  {commande.statut_livraison === 'en_cours' && 'Pris en charge'}
                  {commande.statut_livraison === 'livree' && 'Livraison effectuée'}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-amber-600 mb-3">Aucun livreur assigné</p>
          )}

          <div className="flex gap-2">
            <select value={selectedLivreur} onChange={e => setSelectedLivreur(e.target.value)}
              className="flex-1 px-3 py-2 border border-amber-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
              data-testid="livreur-select">
              <option value="">Sélectionner un livreur...</option>
              {livreurs.map(l => (
                <option key={l.id} value={l.id}>
                  {l.prenom} {l.nom} ({l.email})
                </option>
              ))}
            </select>
            <button onClick={assignerLivreur} disabled={assigningLivreur || !selectedLivreur}
              className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 flex items-center gap-1"
              data-testid="assign-livreur-btn">
              {assigningLivreur ? <RefreshCw size={14} className="animate-spin" /> : <Truck size={14} />}
              Assigner
            </button>
          </div>
        </div>

        {/* Disponibilités */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Clock size={16} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Disponibilités du client</span>
          </div>
          
          {editLivraison ? (
            <div className="space-y-2">
              {disponibilites.map(dispo => {
                const jourInfo = JOURS.find(j => j.key === dispo.jour);
                return (
                  <div key={dispo.jour} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                    <label className="flex items-center gap-2 w-28">
                      <input
                        type="checkbox"
                        checked={dispo.actif}
                        onChange={e => updateDispo(dispo.jour, 'actif', e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-sm font-medium text-gray-700">{jourInfo?.label}</span>
                    </label>
                    {dispo.actif && (
                      <>
                        <input
                          type="time"
                          value={dispo.heure_debut}
                          onChange={e => updateDispo(dispo.jour, 'heure_debut', e.target.value)}
                          className="px-2 py-1 border border-gray-200 rounded text-sm"
                        />
                        <span className="text-gray-400">à</span>
                        <input
                          type="time"
                          value={dispo.heure_fin}
                          onChange={e => updateDispo(dispo.jour, 'heure_fin', e.target.value)}
                          className="px-2 py-1 border border-gray-200 rounded text-sm"
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {disponibilites.filter(d => d.actif).length > 0 ? (
                disponibilites.filter(d => d.actif).map(dispo => {
                  const jourInfo = JOURS.find(j => j.key === dispo.jour);
                  return (
                    <span key={dispo.jour} className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm">
                      {jourInfo?.label} {dispo.heure_debut}-{dispo.heure_fin}
                    </span>
                  );
                })
              ) : (
                <p className="text-sm text-gray-400 italic">Aucune disponibilité renseignée</p>
              )}
            </div>
          )}
        </div>

        {commande.numeros_serie?.length > 0 && (
          <div className="mt-4 p-3 bg-emerald-50 rounded-xl border border-emerald-200">
            <p className="text-xs font-medium text-emerald-700 mb-1">Numéros de série</p>
            <p className="text-sm text-emerald-900 font-mono">{commande.numeros_serie.join(', ')}</p>
          </div>
        )}
      </div>

      {/* Photos d'installation */}
      <div
        className={`bg-white rounded-xl border-2 p-5 shadow-sm transition-colors ${isDragging ? 'border-blue-400 bg-blue-50/50' : 'border-[hsl(214,32%,91%)]'}`}
        data-testid="photos-section"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Camera size={20} className="text-gray-500" />
            <h3 className="font-semibold text-gray-900">Photos d'installation</h3>
            <span className="text-sm text-gray-400">({photos.length})</span>
          </div>
          <label className={`cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${uploadingPhoto ? 'bg-gray-100 text-gray-400' : 'bg-blue-500 text-white hover:bg-blue-600'}`}>
            {uploadingPhoto ? (
              <>
                <RefreshCw size={14} className="animate-spin" />
                Upload...
              </>
            ) : (
              <>
                <Upload size={14} />
                Ajouter
              </>
            )}
            <input 
              type="file" 
              accept="image/*" 
              multiple
              className="hidden" 
              onChange={handleUploadPhoto}
              disabled={uploadingPhoto}
              data-testid="photo-upload-input"
            />
          </label>
        </div>

        {isDragging && (
          <div className="flex flex-col items-center justify-center py-10 mb-4 border-2 border-dashed border-blue-400 rounded-xl bg-blue-50 text-blue-600" data-testid="drop-zone-active">
            <Upload size={32} className="mb-2" />
            <p className="font-medium text-sm">Relâchez pour ajouter les photos</p>
          </div>
        )}

        {!isDragging && photos.length === 0 ? (
          <div className="text-center py-10 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
            <Upload size={40} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">Aucune photo d'installation</p>
            <p className="text-xs mt-1">Glissez-déposez des images ici ou cliquez sur "Ajouter"</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {photos.map(photo => (
              <div key={photo.id} className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-100" data-testid={`photo-${photo.id}`}>
                {/* Image - utiliser thumbnail_url (data URL) ou fallback API */}
                <div 
                  className="aspect-square cursor-pointer flex items-center justify-center"
                  onClick={() => setSelectedPhoto(photo)}
                >
                  <img 
                    src={photo.thumbnail_url || `${api.defaults.baseURL}/documents/${photo.id}/download`}
                    alt={photo.filename_original || 'Photo'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                    }}
                  />
                  <div style={{display:'none'}} className="items-center justify-center w-full h-full text-gray-400 absolute inset-0"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg></div>
                </div>

                {/* Overlay info */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                  <p className="text-white text-xs">{formatPhotoDate(photo.created_at)}</p>
                </div>

                {/* Actions au survol */}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button 
                    onClick={() => setSelectedPhoto(photo)}
                    className="p-1.5 bg-white/90 rounded-lg hover:bg-white shadow-sm"
                    title="Agrandir"
                  >
                    <ZoomIn size={14} className="text-gray-700" />
                  </button>
                  {photo.drive_url && (
                    <a 
                      href={photo.drive_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 bg-white/90 rounded-lg hover:bg-white shadow-sm"
                      title="Ouvrir dans Drive"
                    >
                      <Download size={14} className="text-gray-700" />
                    </a>
                  )}
                  <button 
                    onClick={() => handleDeletePhoto(photo.id)}
                    className="p-1.5 bg-white/90 rounded-lg hover:bg-red-50 shadow-sm"
                    title="Supprimer"
                  >
                    <Trash2 size={14} className="text-red-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Lightbox */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full" onClick={e => e.stopPropagation()}>
            <button 
              onClick={() => setSelectedPhoto(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <X size={24} />
            </button>
            
            <img 
              src={selectedPhoto.thumbnail_url || `${api.defaults.baseURL}/documents/${selectedPhoto.id}/download`}
              alt={selectedPhoto.filename_original || 'Photo'}
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg bg-gray-900"
            />
            
            <div className="mt-3 flex items-center justify-between text-white text-sm">
              <span>{selectedPhoto.filename_original}</span>
              <span>{formatPhotoDate(selectedPhoto.created_at)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Historique */}
      <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4">Historique des statuts</h3>
        {commande.historique?.length > 0 ? (
          <div className="space-y-2">
            {commande.historique.map((h, i) => (
              <div key={`${h.statut}-${h.date}-${i}`} className="flex items-center gap-3 text-sm">
                <span className="w-2 h-2 rounded-full bg-gray-300"></span>
                <StatusBadge statut={h.statut} />
                <span className="text-gray-400">{new Date(h.date).toLocaleString('fr-FR')}</span>
                <span className="text-gray-500">— {h.note || 'Changement de statut'}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic">Aucun historique</p>
        )}
      </div>

      {/* Client card */}
      <div
        onClick={() => navigate(`/clients/${commande.client_id}`)}
        className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-4 shadow-sm flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
        data-testid="client-card"
      >
        <div>
          <p className="font-semibold text-gray-900">{client.societe}</p>
          <p className="text-sm text-gray-500">{client.ville} — {client.siret}</p>
        </div>
        <ArrowLeft size={16} className="text-gray-400 rotate-180" />
      </div>
    </div>
  );
}
